import { useEffect, useState } from 'react';
import type { Service } from '@flashpoint/site-dna';
import type { StepProps } from '../stepProps';
import { INDUSTRY_SUGGESTIONS } from '../wizardSteps';
import { useLocalText } from '../useLocalText';

export function BusinessStep({ lead, queueField }: StepProps) {
  const b = lead.siteDna.businessInfo;
  const [services, setServices] = useState<Service[]>(() => b.services ?? []);

  useEffect(() => {
    setServices(b.services ?? []);
  }, [lead.id]);

  const [name, setName] = useLocalText(lead.id, b.name, 'siteDna.businessInfo.name', queueField);
  const [tagline, setTagline] = useLocalText(
    lead.id,
    b.tagline,
    'siteDna.businessInfo.tagline',
    queueField,
  );
  const [valueProp, setValueProp] = useLocalText(
    lead.id,
    b.valueProp,
    'siteDna.businessInfo.valueProp',
    queueField,
  );
  const [industry, setIndustry] = useLocalText(
    lead.id,
    lead.context.industryVertical,
    'context.industryVertical',
    queueField,
  );

  const updateService = (i: number, patch: Partial<Service>) => {
    const next = [...services];
    next[i] = { ...next[i], ...patch };
    setServices(next);
    queueField('siteDna.businessInfo.services', next);
  };

  const addService = () => {
    const next = [...services, { title: '', description: '', priceLabel: '' }];
    setServices(next);
    queueField('siteDna.businessInfo.services', next);
  };

  const removeService = (i: number) => {
    const next = services.filter((_, idx) => idx !== i);
    setServices(next);
    queueField('siteDna.businessInfo.services', next);
  };

  return (
    <div className="space-y-5">
      <Field label="Business name">
        <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
      </Field>
      <Field label="Tagline">
        <input
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          placeholder="Short, punchy."
          className="input"
        />
      </Field>
      <Field label="Value prop">
        <textarea
          value={valueProp}
          onChange={(e) => setValueProp(e.target.value)}
          rows={3}
          placeholder="One paragraph on what they do and why."
          className="input"
        />
      </Field>
      <Field label="Industry vertical">
        <input
          list="industry-suggestions"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Plumbing"
          className="input"
        />
        <datalist id="industry-suggestions">
          {INDUSTRY_SUGGESTIONS.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </Field>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-widest text-nm-muted">Services</span>
          <button
            type="button"
            onClick={addService}
            className="rounded-md border border-nm-border px-2 py-1 text-xs text-nm-ink hover:border-nm-accent"
          >
            + Add service
          </button>
        </div>
        <div className="space-y-3">
          {services.length === 0 && (
            <p className="rounded-md border border-dashed border-nm-border p-3 text-xs text-nm-muted">
              No services yet. Add at least one - they appear on the Services + Pricing sections.
            </p>
          )}
          {services.map((svc, i) => (
            <div key={i} className="rounded-lg border border-nm-border bg-nm-moss/30 p-3">
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  value={svc.title ?? ''}
                  onChange={(e) => updateService(i, { title: e.target.value })}
                  placeholder="Service title"
                  className="input"
                />
                <input
                  value={svc.priceLabel ?? ''}
                  onChange={(e) => updateService(i, { priceLabel: e.target.value })}
                  placeholder="Price label (e.g. From $149)"
                  className="input"
                />
              </div>
              <textarea
                value={svc.description ?? ''}
                onChange={(e) => updateService(i, { description: e.target.value })}
                rows={2}
                placeholder="Short description"
                className="input mt-2"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeService(i)}
                  className="text-xs text-nm-danger hover:opacity-80"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .input {
          display: block;
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid rgb(51 65 85);
          background-color: rgb(2 6 23);
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: white;
          outline: none;
        }
        .input:focus {
          border-color: #fbbf24;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-widest text-nm-muted">{label}</span>
      {children}
    </label>
  );
}
